import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { RockModel, RockModelManagerData } from '../../core/models/rock-model.model';
import {
  UpscalingRockFileModelDto,
  UpscalingRockFileModelShortDto,
} from '../../core/models/upscaling-rock-file-models.model';

@Injectable({
  providedIn: 'root',
})
export class RockModelsService {
  private readonly apiUrl: string = '/simulation-api';
  private readonly rockModelsUrl: string = `${this.apiUrl}/RockModels`;
  private readonly segmentedRockFileUrl: string = `${this.apiUrl}/SegmentedRockFile`;

  constructor(private readonly httpClient: HttpClient) {}

  getRockModels(fileName: string, options: any = {}, fileType?: string): Observable<RockModelManagerData> {
    let params = new HttpParams();
    if (fileType) {
      params = params.set('fileType', fileType);
    }
    return this.httpClient.get<RockModelManagerData>(`${this.rockModelsUrl}/${fileName}`, {
      headers: options.headers,
      params
    });
  }

  createRockModel(fileName: string, rockModel: RockModel, options: any = {}, fileType?: string): Observable<RockModel> {
    let params = new HttpParams();
    if (fileType) {
      params = params.set('fileType', fileType);
    }
    return this.httpClient.post<RockModel>(`${this.rockModelsUrl}/${fileName}`, rockModel, {
      headers: options.headers,
      params
    });
  }

  updateRockModel(
    fileName: string,
    modelName: string,
    rockModel: RockModel,
    options: any = {},
    fileType?: string
  ): Observable<RockModel> {
    let params = new HttpParams();
    if (fileType) {
      params = params.set('fileType', fileType);
    }
    return this.httpClient.put<RockModel>(`${this.rockModelsUrl}/${fileName}/${modelName}`, rockModel, {
      headers: options.headers,
      params
    });
  }

  duplicateRockModel(fileName: string, modelName: string, options: any = {}, fileType?: string): Observable<RockModel> {
    let params = new HttpParams();
    if (fileType) {
      params = params.set('fileType', fileType);
    }
    return this.httpClient.post<RockModel>(
      `${this.rockModelsUrl}/${fileName}/${modelName}/duplicate`,
      {},
      {
        headers: options.headers,
        params
      }
    );
  }

  deleteRockModel(fileName: string, modelName: string, options: any = {}, fileType?: string): Observable<void> {
    let params = new HttpParams();
    if (fileType) {
      params = params.set('fileType', fileType);
    }
    return this.httpClient.delete<void>(`${this.rockModelsUrl}/${fileName}/${modelName}`, {
      headers: options.headers,
      params
    });
  }

  getUpscalingRockFileModels(fileName: string, options: any = {}): Observable<UpscalingRockFileModelShortDto[]> {
    return this.httpClient.get<UpscalingRockFileModelShortDto[]>(
      `${this.segmentedRockFileUrl}/${encodeURIComponent(fileName)}/models`,
      { headers: options.headers },
    );
  }

  getUpscalingRockFileModel(fileName: string, modelId: string, options: any = {}): Observable<UpscalingRockFileModelDto> {
    return this.httpClient.get<UpscalingRockFileModelDto>(
      `${this.segmentedRockFileUrl}/${encodeURIComponent(fileName)}/model/${encodeURIComponent(modelId)}`,
      { headers: options.headers },
    );
  }

  createUpscalingRockFileModel(
    fileName: string,
    payload: unknown,
    options: any = {},
  ): Observable<UpscalingRockFileModelDto> {
    return this.httpClient.post<UpscalingRockFileModelDto>(
      `${this.segmentedRockFileUrl}/${encodeURIComponent(fileName)}/model`,
      payload,
      { headers: options.headers },
    );
  }

  deleteUpscalingRockFileModel(fileName: string, modelId: string, options: any = {}): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.segmentedRockFileUrl}/${encodeURIComponent(fileName)}/model/${encodeURIComponent(modelId)}`,
      { headers: options.headers },
    );
  }
}
